import React from 'react';
import {
  Bus, MapPin, Ticket, Smartphone, Clock, Shield,
  TrendingUp, Users, Zap, Award, CheckCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';
import './Home.css';

const Home = () => {
  const features = [
    {
      icon: MapPin,
      title: 'Urmărire în Timp Real',
      description: 'Vezi exact unde se află autobuzul tău pe hartă și află timpul exact de sosire.'
    },
    {
      icon: Ticket,
      title: 'Bilete Digitale',
      description: 'Cumpără și validează bilete direct din aplicație, fără cozi și numerar.'
    },
    {
      icon: Clock,
      title: 'Planificare Inteligentă',
      description: 'Calculează cea mai rapidă rută și primește notificări pentru plecări.'
    },
    {
      icon: Shield,
      title: 'Plăți Securizate',
      description: 'Tranzacții 100% sigure cu suport pentru card, Apple Pay și Google Pay.'
    },
    {
      icon: Smartphone,
      title: 'Aplicație Modernă',
      description: 'Interfață intuitivă și ușor de folosit, disponibilă pe iOS și Android.'
    },
    {
      icon: Zap,
      title: 'Actualizări Live',
      description: 'Informații în timp real despre întârzieri, schimbări de traseu și trafic.'
    }
  ];

  const stats = [
    { icon: Bus, value: '30+', label: 'Vehicule Active' },
    { icon: MapPin, value: '10+', label: 'Rute Acoperite' },
    { icon: Users, value: '10K+', label: 'Utilizatori Activi' },
    { icon: Award, value: '4.8★', label: 'Rating Mediu' }
  ];

  const benefits = [
    'Economisești timp și bani',
    'Călătorii confortabile și sigure',
    'Suport dedicat 24/7',
    'Abonamente flexibile',
    'Reduceri pentru studenți și pensionari',
    'Aplicație gratuită'
  ];

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-background">
          <div className="hero-overlay"></div>
        </div>
        <div className="container hero-container">
          <div className="hero-content fade-in">
            <h1 className="hero-title">
              Călătorește <span className="hero-highlight">Smart</span><br />
              cu Multipower Tour  
            </h1>
            <p className="hero-subtitle">
              Planifică călătoria, cumpără bilete online și urmărește autobuzul în timp real.
              Totul într-o singură aplicație simplă și intuitivă.
            </p>
            <div className="hero-actions">
              <Link to="/bilete" className="btn btn-primary btn-large">
                <Ticket size={20} />
                Cumpără Bilet
              </Link>
              <Link to="/rute" className="btn btn-secondary btn-large">
                <MapPin size={20} />
                Vezi Rutele
              </Link>
            </div>

            {/* Quick Stats */}
            <div className="hero-stats">
              {stats.map((stat, index) => (
                <div key={index} className="hero-stat">
                  <stat.icon size={24} />
                  <div>
                    <div className="hero-stat-value">{stat.value}</div>
                    <div className="hero-stat-label">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Hero Image/Illustration */}
          <div className="hero-image">
            <img src={require('../assets/iphone.png')} alt="App Preview" className="hero-phone-mockup" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section features-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">De ce să alegi aplicația noastră?</h2>
            <p className="section-subtitle">
              Tot ce ai nevoie pentru o călătorie perfectă, într-un singur loc
            </p>
          </div>

          <div className="grid grid-3">
            {features.map((feature, index) => (
              <div key={index} className="feature-card card fade-in">
                <div className="feature-icon">
                  <feature.icon size={28} />
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="section how-it-works-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Cum Funcționează?</h2>
            <p className="section-subtitle">Simplu, rapid și eficient - în doar 4 pași</p>
          </div>

          <div className="steps-container">
            <div className="step">
              <div className="step-number">
                <Smartphone size={48} strokeWidth={2.5} />
              </div>
              <div className="step-badge">Pasul 1</div>
              <div className="step-content">
                <h3>Descarcă Aplicația</h3>
                <p>Disponibilă gratuit pe iOS și Android. Instalare rapidă în 30 de secunde.</p>
              </div>
            </div>

            <div className="step">
              <div className="step-number">
                <MapPin size={48} strokeWidth={2.5} />
              </div>
              <div className="step-badge">Pasul 2</div>
              <div className="step-content">
                <h3>Alege Ruta</h3>
                <p>Caută destinația și vezi toate opțiunile cu timpi de sosire în timp real.</p>
              </div>
            </div>

            <div className="step">
              <div className="step-number">
                <Ticket size={48} strokeWidth={2.5} />
              </div>
              <div className="step-badge">Pasul 3</div>
              <div className="step-content">
                <h3>Cumpără Biletul</h3>
                <p>Plată rapidă și securizată cu card, Apple Pay sau Google Pay.</p>
              </div>
            </div>

            <div className="step">
              <div className="step-number">
                <Bus size={48} strokeWidth={2.5} />
              </div>
              <div className="step-badge">Pasul 4</div>
              <div className="step-content">
                <h3>Călătorește</h3>
                <p>Validează biletul digital și bucură-te de o călătorie confortabilă.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section benefits-section">
        <div className="container">
          <div className="benefits-grid">
            <div className="benefits-content">
              <h2 className="section-title">Beneficii pentru Călătorii</h2>
              <p className="section-subtitle" style={{ textAlign: 'left', marginLeft: 0 }}>
                Peste 1,000 de oameni ne folosesc zilnic aplicația pentru a călători mai ușor
              </p>

              <ul className="benefits-list">
                {benefits.map((benefit, index) => (
                  <li key={index} className="benefit-item">
                    <CheckCircle size={20} />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>

              <Link to="/bilete" className="btn btn-primary btn-large">
                Începe Acum
              </Link>
            </div>

            <div className="benefits-image">
              <div className="benefits-card card">
                <TrendingUp size={48} />
                <h3>Economisești Timp</h3>
                <p className="big-stat">~30 min</p>
                <p>pe zi față de așteptarea la coadă</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section cta-section">
        <div className="container">
          <div className="cta-card">
            <h2 className="cta-title">Gata să călătorești mai smart?</h2>
            <p className="cta-subtitle">
              Descarcă aplicația acum
            </p>
            <div className="cta-buttons">
              <a target="_blank" href="https://apps.apple.com/ro/app/multipower-tour/id6755851805?l=ro" className="store-button">
                <img src={require('../assets/badge_appstoreios.webp')} alt="Download on App Store" />
              </a>

              <a target="_blank" href="https://play.google.com/store/apps/details?id=ro.multipowertour" className="store-button">
                <img src={require('../assets/badgle_googleplay.webp')} alt="Get it on Google Play" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
